<template>
  <div class="input-mensagem border-top p-2">
    <div class="d-flex align-items-center">
      <!-- Dropdown de Anexos -->
      <div class="dropdown-anexos-container mr-2" v-click-outside="fecharDropdown">
        <button
          class="btn btn-light btn-sm dropdown-anexos-toggle"
          @click="toggleDropdown"
        >
          <i class="feather icon-plus"></i>
        </button>
        <div class="dropdown-menu-anexos" :class="{ show: dropdownAberto }">
          <a class="dropdown-item" href="#" @click.prevent="abrirInputArquivo">
            <i class="feather icon-paperclip mr-2"></i>
            Anexar arquivo
          </a>
        </div>
      </div>

      <!-- Input de arquivo (oculto) -->
      <input
        ref="inputArquivo"
        type="file"
        accept="image/*,video/*,audio/*,application/pdf"
        style="display: none"
        @change="onArquivoSelecionado"
      />

      <!-- Campo de texto -->
      <b-form-input
        v-model="mensagem"
        placeholder="Digite sua mensagem..."
        class="flex-grow-1 mr-2"
        @keydown.enter.prevent="enviar"
      />

      <!-- Botão de enviar -->
      <b-button
        variant="primary"
        :disabled="!mensagem.trim()"
        @click="enviar"
      >
        <feather-icon
          icon="SendIcon"
          size="18"
        />
      </b-button>
    </div>

    <!-- Preview do arquivo selecionado -->
    <div
      v-if="arquivoSelecionado"
      class="preview-arquivo mt-2 p-2 bg-light rounded d-flex align-items-center"
    >
      <div class="flex-grow-1">
        <div class="d-flex align-items-center">
          <feather-icon
            :icon="getIconeArquivo(arquivoSelecionado)"
            size="20"
            class="mr-2"
          />
          <div>
            <div class="font-weight-bold">{{ arquivoSelecionado.name }}</div>
            <small class="text-muted">{{ formatarTamanho(arquivoSelecionado.size) }}</small>
          </div>
        </div>
        
        <!-- Input para texto opcional -->
        <b-form-input
          v-model="textoMidia"
          placeholder="Adicionar legenda (opcional)..."
          class="mt-2"
          size="sm"
        />
      </div>

      <!-- Botões de ação -->
      <div class="ml-2 d-flex flex-column">
        <b-button
          variant="danger"
          size="sm"
          class="mb-1"
          @click="cancelarArquivo"
        >
          <feather-icon
            icon="XIcon"
            size="14"
          />
        </b-button>
        <b-button
          variant="success"
          size="sm"
          @click="enviarArquivo"
        >
          <feather-icon
            icon="SendIcon"
            size="14"
          />
        </b-button>
      </div>
    </div>

    <!-- Barra de progresso -->
    <b-progress
      v-if="enviandoArquivo"
      :value="progressoUpload"
      class="mt-2"
      height="8px"
      variant="primary"
      striped
      animated
    />
  </div>
</template>

<script>
import { BButton, BFormInput, BProgress } from 'bootstrap-vue'
import { FeatherIcon } from 'vue-feather-icons'

export default {
  components: {
    BButton,
    BFormInput,
    BProgress,
    FeatherIcon,
  },
  data() {
    return {
      mensagem: '',
      arquivoSelecionado: null,
      textoMidia: '',
      enviandoArquivo: false,
      progressoUpload: 0,
      dropdownAberto: false,
    }
  },
  directives: {
    'click-outside': {
      bind(el, binding) {
        el.clickOutsideEvent = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value()
          }
        }
        document.addEventListener('click', el.clickOutsideEvent)
      },
      unbind(el) {
        document.removeEventListener('click', el.clickOutsideEvent)
      },
    },
  },
  methods: {
    toggleDropdown() {
      this.dropdownAberto = !this.dropdownAberto
    },
    fecharDropdown() {
      this.dropdownAberto = false
    },
    abrirInputArquivo() {
      this.$refs.inputArquivo.click()
      this.fecharDropdown()
    },
    enviar() {
      if (!this.mensagem.trim()) return
      this.$emit('enviar', this.mensagem.trim())
      this.mensagem = ''
    },
    onArquivoSelecionado(event) {
      const arquivo = event.target.files[0]
      if (!arquivo) return

      // Validar tamanho (máximo 100MB)
      const tamanhoMaxMB = 100
      const tamanhoMaxBytes = tamanhoMaxMB * 1024 * 1024
      
      if (arquivo.size > tamanhoMaxBytes) {
        this.$bvToast.toast(`O arquivo excede o tamanho máximo de ${tamanhoMaxMB}MB`, {
          title: 'Arquivo muito grande',
          variant: 'warning',
          solid: true,
        })
        event.target.value = null
        return
      }

      this.arquivoSelecionado = arquivo
    },
    cancelarArquivo() {
      this.arquivoSelecionado = null
      this.textoMidia = ''
      this.$refs.inputArquivo.value = null
    },
    async enviarArquivo() {
      if (!this.arquivoSelecionado) return

      this.enviandoArquivo = true
      this.progressoUpload = 0

      try {
        await this.$emit('enviar-midia', {
          arquivo: this.arquivoSelecionado,
          texto: this.textoMidia,
          onProgress: (progressEvent) => {
            this.progressoUpload = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          },
        })

        // Limpar após envio
        this.cancelarArquivo()
      } catch (error) {
        console.error('Erro ao enviar arquivo:', error)
        this.$bvToast.toast('Erro ao enviar arquivo. Tente novamente.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.enviandoArquivo = false
        this.progressoUpload = 0
      }
    },
    getIconeArquivo(arquivo) {
      const tipo = arquivo.type
      if (tipo.startsWith('image/')) return 'ImageIcon'
      if (tipo.startsWith('video/')) return 'VideoIcon'
      if (tipo.startsWith('audio/')) return 'MusicIcon'
      if (tipo === 'application/pdf') return 'FileTextIcon'
      return 'FileIcon'
    },
    formatarTamanho(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const tamanhos = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + tamanhos[i]
    },
  },
}
</script>

<style scoped>
.preview-arquivo {
  border: 1px solid #dee2e6;
}

/* Dropdown de anexos */
.dropdown-anexos-container {
  position: relative;
  display: inline-block;
}

.dropdown-anexos-toggle {
  border-radius: 50%;
  width: 36px;
  height: 36px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.dropdown-anexos-toggle:hover {
  background-color: #f0f0f0;
  transform: scale(1.05);
}

.dropdown-menu-anexos {
  position: absolute;
  bottom: 45px;
  left: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 180px;
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px);
  transition: all 0.2s ease;
}

.dropdown-menu-anexos.show {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-item {
  padding: 10px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #333;
  text-decoration: none;
  transition: background-color 0.2s;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
  color: #7367f0;
}

.dropdown-item i {
  font-size: 16px;
}
</style>
